import type { LocationGroup } from './types';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

const MP_TOKEN_KEY = 'mpp-widgets_AuthToken';

function chubHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

/**
 * The MinistryPlatform widget login token. MP's `<mpp-user-login>` widget on
 * the host page puts it in localStorage; null when nobody is signed in.
 */
export function getMpWidgetToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const token = localStorage.getItem(MP_TOKEN_KEY);
    return token && token !== 'null' ? token : null;
  } catch {
    return null;
  }
}

/** Fetch all campuses, grouped by location, from the API */
export async function fetchLocations(apiBaseUrl: string): Promise<LocationGroup[]> {
  const res = await fetch(`${apiBaseUrl}/api/locations`, { headers: chubHeaders() });
  if (!res.ok) throw new Error(`API returned ${res.status}`);
  return res.json();
}

export interface HomeCampus {
  congregationId: number | null;
  /** 'web' = Contacts.Web_Congregation_ID, 'household' = the household's campus */
  source: 'web' | 'household' | null;
}

/**
 * The signed-in person's campus in MinistryPlatform. Null when signed out or
 * when the token is rejected, so callers fall back to the cookie.
 */
export async function fetchHomeCampus(apiBaseUrl: string): Promise<HomeCampus | null> {
  const token = getMpWidgetToken();
  if (!token) return null;
  try {
    const res = await mpFetch(`${apiBaseUrl}/api/user/home-campus`, {
      headers: { ...chubHeaders(), Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as HomeCampus;
  } catch (err) {
    console.error('CampusPicker: could not read home campus', err);
    return null;
  }
}

/** Persist the campus to MinistryPlatform (Contacts.Web_Congregation_ID). No-op when signed out. */
export async function setHomeCampus(apiBaseUrl: string, congregationId: number): Promise<boolean> {
  const token = getMpWidgetToken();
  if (!token) return false;

  try {
    const res = await mpFetch(`${apiBaseUrl}/api/set-home-campus`, {
      method: 'POST',
      headers: {
        ...chubHeaders(),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ congregationId }),
    });
    if (!res.ok) console.warn(`CampusPicker: set-home-campus returned ${res.status}`);
    return res.ok;
  } catch (err) {
    console.error('CampusPicker: error setting home campus', err);
    return false;
  }
}

export interface CampusLive {
  isLive: boolean;
  watchUrl: string;
}

/**
 * Which campuses are livestreaming right now (Sardius, via the API), keyed by
 * Congregation_ID. Empty on any failure: the dot is a nicety, never a blocker.
 */
export async function fetchLiveStatus(apiBaseUrl: string): Promise<Map<number, CampusLive>> {
  const map = new Map<number, CampusLive>();
  try {
    const res = await fetch(`${apiBaseUrl}/api/live-status?all=1`, { headers: chubHeaders() });
    if (!res.ok) return map;
    const data: {
      campuses?: Array<{ congregationId: number; isLive: boolean; watchUrl: string }>;
    } = await res.json();
    for (const c of data.campuses ?? []) {
      map.set(c.congregationId, { isLive: c.isLive, watchUrl: c.watchUrl });
    }
  } catch {
    // leave the map empty
  }
  return map;
}

export interface CampusAnnouncement {
  id: number;
  title: string;
  image_url: string | null;
  link: string | null;
}

export interface CampusAnnouncements {
  items: CampusAnnouncement[];
  /** How many the campus's carousel has in all, the previews included */
  total: number;
}

/** Announcements are fetched once per campus per tab session, then kept this long */
const ANNOUNCEMENTS_TTL_MS = 10 * 60 * 1000;
const ANNOUNCEMENTS_KEY = 'campus-picker:announcements:v2:';
const NO_ANNOUNCEMENTS: CampusAnnouncements = { items: [], total: 0 };

/**
 * The first few announcements on a campus's home page carousel, in its
 * order, and how many it has in all. Kept in sessionStorage so moving around
 * the site does not refetch. Empty on any failure: the previews are a
 * nicety, never a blocker.
 */
export async function fetchCampusAnnouncements(
  apiBaseUrl: string,
  congregationId: number,
  limit = 3
): Promise<CampusAnnouncements> {
  const key = `${ANNOUNCEMENTS_KEY}${congregationId}:${limit}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) ?? 'null') as
      | (CampusAnnouncements & { at: number })
      | null;
    if (cached && Date.now() - cached.at < ANNOUNCEMENTS_TTL_MS) {
      return { items: cached.items, total: cached.total };
    }
  } catch {
    // no storage, or a bad entry: fetch
  }
  try {
    const res = await fetch(
      `${apiBaseUrl}/api/announcements?congregationId=${congregationId}&limit=${limit}`,
      { headers: chubHeaders() }
    );
    if (!res.ok) return NO_ANNOUNCEMENTS;
    const data: { announcements?: CampusAnnouncement[]; total?: number } = await res.json();
    const items = data.announcements ?? [];
    const result = { items, total: Math.max(data.total ?? items.length, items.length) };
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), ...result }));
    } catch {
      // storage full or blocked
    }
    return result;
  } catch {
    return NO_ANNOUNCEMENTS;
  }
}
